import { PageController } from '@client/components/_core/layout/PageController';
import { getConfig } from '@client/config/runtime';
import type { FileRouteTypes } from '@client/routeTree.gen';
import { useAuthStore } from '@client/stores/AuthStore';
import { loginWithRedirect } from '@client/utils/auth/auth0Client';
import { debug } from '@client/utils/logger';
import { extractPathParams } from '@client/utils/routing/extractPathParams';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
    redirectTo: z.string().optional(),
    orgId: z.coerce.bigint().optional(),
    forceReauth: z.literal(1).optional(),
});

export const Route = createFileRoute('/(authentication)/login')({
    validateSearch: zodValidator(searchSchema),
    component: Login,
});

function Login() {
    const { redirectTo, orgId, forceReauth } = Route.useSearch();
    const authStore = useAuthStore();

    useEffect(() => {
        // redirectTo might be https://curium.app/console/123?param=value#hash — strip the query
        // and the hash before reading the organisation out of the path.
        const decodedRedirectTo = safeDecodeURIComponent(redirectTo ?? '');
        const redirectToWithoutQuery = decodedRedirectTo.split(/[?#]/)[0] ?? '';
        const params = extractPathParams(redirectToWithoutQuery, '/console/$orgId', { fuzzy: true });
        let orgIdParam: bigint | undefined;
        if (orgId) {
            orgIdParam = orgId;
        } else if (params?.orgId) {
            orgIdParam = parseOrgIdParam(params.orgId);
        } else if (decodedRedirectTo.startsWith('/admin-console' satisfies FileRouteTypes['to'])) {
            orgIdParam = SYSTEM_ORG_IDS.SYSTEM_CONSOLE;
        }

        const orgIdHint = orgIdParam?.toString();

        if (forceReauth === 1) {
            debug('[Login] Clearing auth token');
            authStore.clearAuthToken();
        }

        void loginWithRedirect({
            authorizationParams: {
                audience: getConfig('AUTH0_AUDIENCE', import.meta.env.VITE_AUTH0_AUDIENCE),
                scope: 'openid profile email offline_access',
                isLogin: true,
                ...(orgIdHint && { orgId: orgIdHint }),
            },
            appState: { redirectTo: redirectTo || '', ...(orgIdHint && { requestOrgId: orgIdHint }) },
            forceReauth: forceReauth === 1,
        });
    }, []);

    return <PageController route={Route}></PageController>;
}

function safeDecodeURIComponent(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function parseOrgIdParam(value: string): bigint | undefined {
    if (!/^-?\d+$/.test(value)) return undefined;
    return BigInt(value);
}
